'use client';
import { AriaButtonProps, useButton } from '@react-aria/button';
import { cva, VariantProps } from 'class-variance-authority';
import { motion, MotionProps } from 'framer-motion';
import { ButtonHTMLAttributes, forwardRef, HTMLAttributes, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '~/lib/cn';
// From the module, not from the package barrel next door. `from '.'` made every
// Button pull `./index`, and the barrel re-exported the whole UI set — so a public
// club page that only wanted a button reached the map library through it.
import ButtonLink from './ButtonLink';
import ButtonCard, { type ButtonCardProps } from './ButtonCard';
import Spinner from './Spinner';

export const buttonClassNames = cva(
    'tw-font-normal tw-min-w-[28px] tw-flex tw-gap-2 tw-whitespace-nowrap tw-items-center tw-transition-colors tw-justify-center tw-border tw-border-solid tw-leading-none focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-accents-9 focus-visible:tw-ring-offset-1',
    {
        variants: {
            color: {
                light: '',
                default: '',
                primary: '',
                secondary: '',
                success: '',
                warning: '',
                error: '',
                gradient: ''
            },
            flat: {
                true: '',
                false: 'tw-border-transparent'
            },
            block: {
                true: 'tw-w-full',
                false: 'tw-w-auto'
            },
            disabled: {
                true: 'tw-cursor-not-allowed tw-opacity-50',
                false: 'tw-cursor-pointer'
            },
            size: {
                xs: 'tw-h-6 tw-text-xs tw-px-2 tw-py-1 tw-rounded-lg',
                sm: 'tw-h-8 tw-max-h-9 tw-text-sm tw-px-4 tw-leading-6 tw-py-2 tw-rounded-xl',
                md: 'tw-h-10 tw-text-base tw-px-4 tw-py-2.5 tw-rounded-xl',
                lg: 'tw-h-12 tw-text-lg tw-px-5 tw-py-3 tw-rounded-xl',
                xl: 'tw-h-16 tw-text-xl tw-px-6 tw-py-4 tw-rounded-xl'
            },
            // after size on purpose: cn lets these win over the fixed height and nowrap
            multiline: {
                true: 'tw-h-auto tw-whitespace-normal tw-text-center tw-leading-tight',
                false: ''
            }
        },
        compoundVariants: [
            {
                className:
                    'tw-bg-successLight/70 hover:tw-text-success tw-text-success hover:tw-bg-successLight/100 tw-border-success/10',
                flat: true,
                color: ['success', 'primary', 'default']
            },
            {
                className: 'tw-bg-success hover:tw-text-white tw-text-white hover:tw-bg-success/80',
                flat: false,
                color: ['success', 'primary', 'default']
            },
            {
                className:
                    'tw-bg-errorLight/80 hover:tw-text-error tw-text-error hover:tw-bg-errorLight/100 tw-border-error/10',
                flat: true,
                color: 'error'
            },
            {
                className: 'tw-bg-error hover:tw-text-white tw-text-white hover:tw-bg-error/80',
                flat: false,
                color: 'error'
            },
            {
                className:
                    'tw-bg-warningLight/80 hover:tw-text-warning tw-text-warning hover:tw-bg-warningLight/100 tw-border-warning/10',
                flat: true,
                color: 'warning'
            },
            {
                className: 'tw-bg-warning hover:tw-text-white tw-text-white hover:tw-bg-warning/80',
                flat: false,
                color: 'warning'
            },
            {
                className:
                    'tw-bg-accents-2 hover:tw-text-accents-7 tw-text-accents-7 hover:tw-bg-accents-3 tw-border-accents-7/10',
                flat: true,
                color: ['secondary', 'gradient']
            },
            {
                className: 'tw-bg-accents-6 hover:tw-text-white tw-text-white hover:tw-bg-accents-6/80',
                flat: false,
                color: ['secondary', 'gradient']
            },
            {
                className:
                    'tw-bg-accents-0 hover:tw-text-accents-7 tw-text-accents-7 hover:tw-bg-accents-1 tw-border-accents-7/10',
                flat: true,
                color: ['light']
            },
            {
                className: 'tw-bg-accents-2 hover:tw-text-accents-7 tw-text-accents-7 hover:tw-bg-accents-0',
                flat: false,
                color: ['light']
            }
        ],
        defaultVariants: {
            color: 'success',
            block: false,
            size: 'sm',
            flat: false,
            disabled: false,
            multiline: false
        }
    }
);

type ButtonVariantProps = VariantProps<typeof buttonClassNames>;

type ButtonSubComponents = {
    Link: typeof ButtonLink;
    Card: typeof ButtonCard;
};

interface Props
    extends
        AriaButtonProps,
        ButtonVariantProps,
        Omit<HTMLAttributes<HTMLButtonElement>, keyof ButtonVariantProps | keyof AriaButtonProps | keyof MotionProps> {
    isLoading?: boolean;
    icon?: React.ReactNode;
    iconRight?: React.ReactNode;
    isDisabled?: boolean;
    disableAnimation?: boolean;

    /**
     * Runs a progress bar inside the button and presses it automatically after this many
     * milliseconds, unless the user gets there first. For screens that should move on by
     * themselves — a kiosk confirmation nobody is going to tap.
     */
    autoCloseMs?: number;

    /** @deprecated use color="light" instead */
    light?: boolean;

    /** @deprecated use className and style instead */
    css?: any;

    /** @deprecated use custom className instead */
    auto?: any;

    /** @deprecated use custom className instead */
    as?: any;
}

export const Button = forwardRef<HTMLButtonElement, Props>(
    (
        {
            isLoading,
            block,
            color,
            flat,
            disabled,
            size,
            multiline,
            isDisabled,
            light,
            iconRight,
            className,
            icon,
            onPress,
            disableAnimation,
            autoCloseMs,
            ...props
        },
        _ref
    ) => {
        const ref = useRef(null);
        const { buttonProps, isPressed } = useButton({ ...props, onPress }, ref);

        const animationVariants = {
            pressed: { scale: 0.98 },
            unpressed: { scale: 1 }
        };

        useImperativeHandle(_ref, () => ref.current!, []);

        // held in a ref so an inline onPress prop does not restart the countdown on every render
        const onPressRef = useRef(onPress);
        useEffect(() => {
            onPressRef.current = onPress;
        });

        const isInactive = Boolean(isLoading || disabled || isDisabled);

        // presses the button on the user's behalf once the bar has run out
        useEffect(() => {
            if (!autoCloseMs || isInactive) return;
            const timeout = setTimeout(
                () => onPressRef.current?.({ type: 'press', pointerType: 'virtual' } as any),
                autoCloseMs
            );
            return () => clearTimeout(timeout);
        }, [autoCloseMs, isInactive]);

        return (
            <motion.button
                animate={!disableAnimation ? (!disabled && isPressed ? 'pressed' : 'unpressed') : undefined}
                variants={animationVariants}
                className={cn(
                    buttonClassNames({
                        color: light ? 'light' : color,
                        block,
                        size,
                        multiline,
                        flat,
                        disabled: disabled || isLoading || isDisabled
                    }),
                    // the bar is positioned against the button, so it needs a containing block
                    autoCloseMs && 'tw-relative tw-overflow-hidden',
                    className
                )}
                {...props}
                {...(buttonProps as ButtonHTMLAttributes<HTMLButtonElement> & MotionProps)}
                ref={ref}
                disabled={isLoading || disabled || isDisabled}
            >
                {!isLoading && icon && <span className="tw-scale-[85%]">{icon}</span>}
                {isLoading ? (
                    <Spinner size={size} />
                ) : (
                    props.children
                )}
                {!isLoading && iconRight && <span className="tw-scale-[85%]">{iconRight}</span>}
                {!!autoCloseMs && !isInactive && (
                    <motion.span
                        className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-h-[3px] tw-bg-current tw-opacity-40"
                        initial={{ width: '100%' }}
                        animate={{ width: '0%' }}
                        transition={{ duration: autoCloseMs / 1000, ease: 'linear' }}
                    />
                )}
            </motion.button>
        );
    }
) as React.ForwardRefExoticComponent<Props & React.RefAttributes<HTMLButtonElement>> & ButtonSubComponents;

Button.displayName = 'Button';
Button.Link = ButtonLink;
Button.Card = ButtonCard;

export type { ButtonCardProps, Props as ButtonProps };
